// The in-memory API's agents (AG; WORKFORCE, services/api/src/routes/org/workforce.ts), with the
// real routes' rules: every member lists and opens them (no add-on gate); a manager, the person
// an agent reports to, or a holder of its DE's seat or any seat up that chain changes it (others
// 403); the target of a reassignment is an active person, or a non-retired seated DE whose chain
// ends at a person (else 422 supervisor_required); the same values again write nothing. The twin's company Acme
// has a few agents under its second seed DE, so the Workforce screens and the seat tree have content.
import { defaultProfile, routes } from "@companyos/schema/contracts";
import type { OperationOutput } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem, validation } from "./fake";
import type { FakeDe } from "./fake-data";
import { COMPANY_IDS, uid } from "./fake-data";
import { fakePurgePlan, fakePurgeResult, sameName } from "./fake-purge";

type Agent = OperationOutput<"workforceAgents.get">;
type Member = { id: string; name: string; kind: "person" | "digital_employee" };

const EPOCH = "2026-10-20T09:00:00.000Z";

/**
 * A write by which a DE leaves, as production's `assertNoneStranded` sees it after the write:
 * `retire` memberships leave every seat (a DE retired or removed), `drop` ends single
 * assignments (a DE unassigned), `closeSeats` closes seats (and ends their assignments).
 * `seatIds` and `membershipIds` are what the write touched (production's arguments): the seats
 * the touched memberships held count as touched, and their agents are checked.
 */
export type Leave = {
  membershipIds?: readonly string[];
  retire?: readonly string[];
  seatIds?: readonly string[];
  drop?: readonly { seat_id: string; membership_id: string }[];
  closeSeats?: readonly string[];
};

export function createWorkforceHandlers(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  /** The company's people; `status` other than "active" is not a valid reports-to target. */
  peopleOf: (p: Principal) => { id: string; name: string; status?: string }[];
  teamsOf?: (company: Principal["company"]) => { id: string; name: string }[];
  /** Setup › Seats' stores (the seat chain); without them a DE's seed supervisor stands in. */
  seatsOf?: (
    company: Principal["company"],
  ) => { id: string; supervisor_seat_id: string | null; status?: string }[];
  assignmentsOf?: (company: Principal["company"]) => {
    seat_id: string;
    membership_id: string;
    membership_kind: "person" | "digital_employee";
    active_to: string | null;
  }[];
  /** WF-CONTROL: records a profile revision (create: `before` null; no change: nothing). */
  revise?: (p: Principal, id: string, before: Agent | null, after: Agent) => void;
  /** Hands the DE and seat handlers the guards they need (production's `assertNoneStranded`). */
  expose?: (api: {
    liveAgentsOf: (p: Principal, membershipId: string) => string[];
    /** BILLING-PLANS: the same agents with their ids (a DE's AGs ride its maintenance). */
    liveAgentRowsOf: (p: Principal, membershipId: string) => { id: string; name: string }[];
    /** ACTIVITY: whom an agent reports to (Activity's Who = an AG filters its DE's runs). */
    reportsToOf: (p: Principal, agentId: string) => string | null;
    strandedAfter: (p: Principal, leave: Leave) => string[];
    clearTeam: (p: Principal, teamId: string) => void;
    /** CHART-AGENTS: the live agents for the org chart (fake-teams.ts cuts them to its holders). */
    chartAgentsOf: (p: Principal) => OperationOutput<"orgChart.get">["agents"];
    /** WEBHOOKS-IN: an agent of the company (a hook's "for agent" reports to its DE). */
    agentOf: (
      p: Principal,
      id: string,
    ) => { name: string; reports_to_membership_id: string } | null;
  }) => void;
}): Record<string, Handler> {
  const store = new Map<string, Agent[]>();
  const meOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  /** The active holders of a seat (the stores, else the seed: a DE on its seat, its supervisor). */
  const holdersOf = (p: Principal, seatId: string) => {
    const rows = (deps.assignmentsOf?.(p.company) ?? []).filter(
      (a) => a.seat_id === seatId && a.active_to === null,
    );
    if (rows.length || deps.assignmentsOf) return rows;
    const des = deps.desOf(p.company.id);
    return [
      ...des
        .filter((d) => d.seat_id === seatId)
        .map((d) => ({ membership_id: d.membership_id, membership_kind: "digital_employee" })),
      ...des
        .filter((d) => d.supervisor.seat_id === seatId)
        .slice(0, 1)
        .map((d) => ({ membership_id: d.supervisor.membership_id, membership_kind: "person" })),
    ];
  };
  const supervisorSeatOf = (p: Principal, seatId: string): string | null => {
    const s = deps.seatsOf?.(p.company).find((x) => x.id === seatId);
    if (s) return s.supervisor_seat_id;
    return deps.desOf(p.company.id).find((d) => d.seat_id === seatId)?.supervisor.seat_id ?? null;
  };
  const liveDe = (p: Principal, id: string) =>
    deps.desOf(p.company.id).find((d) => d.membership_id === id && d.status !== "retired");
  /** The reporting rule: up from a seat through DE-held seats (non-retired DEs) to a person. */
  const chainEndsAtPerson = (p: Principal, seatId: string): boolean => {
    const seen = new Set<string>();
    let at: string | null = seatId;
    while (at !== null && !seen.has(at)) {
      seen.add(at);
      const holders = holdersOf(p, at);
      if (holders.some((h) => h.membership_kind === "person" && activePerson(p, h.membership_id)))
        return true;
      if (
        !holders.some((h) => h.membership_kind === "digital_employee" && liveDe(p, h.membership_id))
      )
        return false;
      at = supervisorSeatOf(p, at);
    }
    return false;
  };
  const activePerson = (p: Principal, id: string) =>
    meOf(p) === id ||
    deps.peopleOf(p).some((x) => x.id === id && (x.status ?? "active") === "active");
  const membersOf = (p: Principal): Member[] => {
    const out: Member[] = [
      ...deps
        .desOf(p.company.id)
        .filter((d) => d.status !== "retired")
        .map((d) => ({
          id: d.membership_id,
          name: d.display_name,
          kind: "digital_employee" as const,
        })),
      ...deps
        .peopleOf(p)
        .filter((x) => (x.status ?? "active") === "active")
        .map((x) => ({ id: x.id, name: x.name, kind: "person" as const })),
    ];
    // The demo account's own person membership (the generated people list leaves it out).
    const me = meOf(p);
    if (me && !out.some((m) => m.id === me))
      out.push({ id: me, name: p.me.user.name || p.me.user.email, kind: "person" });
    return out;
  };

  function agentsOf(p: Principal): Agent[] {
    let list = store.get(p.company.id);
    if (!list) {
      list = [];
      // Under the company's SECOND seed DE (Sam in Acme), whatever was created or retired since:
      // the twin's tests retire and remove the first (Riley) and DEs they create, which the
      // stranded-chain guard would refuse while agents report to them.
      const all = deps.desOf(p.company.id);
      const lead = [all[1], ...all].find((d) => d && d.status !== "retired");
      if (p.company.id === COMPANY_IDS.acme && lead) {
        const team = deps.teamsOf?.(p.company)[0] ?? null;
        const mk = (
          n: number,
          slug: string,
          name: string,
          activity: string | null,
          role: string | null = null,
          skills: string[] = [],
        ): Agent => ({
          company_id: p.company.id,
          id: uid(0x7a00, n),
          slug,
          name,
          team_id: team?.id ?? null,
          team: team ? { id: team.id, name: team.name } : null,
          runtime: {
            system: "hermes",
            container: "hermes-acme-studio-1",
            profile: slug,
            clawpanel_instance: "1",
          },
          reports_to_membership_id: lead.membership_id,
          reports_to_kind: "digital_employee",
          reports_to: {
            membership_id: lead.membership_id,
            kind: "digital_employee",
            name: lead.display_name,
          },
          status: "active",
          last_activity_at: activity ? EPOCH : null,
          last_activity: activity,
          role,
          goal: null,
          backstory: null,
          responsibilities: [],
          standing_orders: null,
          skills,
          can_edit: false,
          used_by: [],
          created_at: EPOCH,
          updated_at: EPOCH,
        });
        list.push(
          mk(
            1,
            "text-post-writer",
            "Text Post Writer",
            "Drafted a post for review",
            "Writes short text posts from the brief.",
            ["text-post", "hooks"],
          ),
          // SKILL-OVERRIDES: one of our skills (a core pack) beside its self-hosted one. Local
          // in-memory demo only (Acme in the fake twin); no real company's seed or data has it.
          mk(2, "voice-judge", "Voice Judge", null, null, ["brand-voice", "research-brief"]),
        );
      }
      store.set(p.company.id, list);
    }
    return list;
  }

  /**
   * Who may change an agent (the API's rule): a company manager; the person it reports to; or,
   * when it reports to a DE, whoever holds that DE's seat or any seat up its chain (any depth).
   */
  const canEdit = (p: Principal, a: Agent): boolean => {
    if (p.manager) return true;
    const me = meOf(p);
    if (!me) return false;
    if (a.reports_to_kind === "person") {
      if (a.reports_to_membership_id === me) return true;
      // The person's supervisor: one whose seat directly supervises a seat that person holds.
      return seatsHeldBy(p, a.reports_to_membership_id).some((s) => {
        const sup = supervisorSeatOf(p, s);
        return sup !== null && holdersOf(p, sup).some((h) => h.membership_id === me);
      });
    }
    const de = deps.desOf(p.company.id).find((d) => d.membership_id === a.reports_to_membership_id);
    if (!de) return false;
    const seen = new Set<string>();
    let at: string | null = de.seat_id;
    while (at !== null && !seen.has(at)) {
      seen.add(at);
      if (holdersOf(p, at).some((h) => h.membership_id === me)) return true;
      at = supervisorSeatOf(p, at);
    }
    return false;
  };
  /** The seats a member actively holds (the stores, else the seed's supervisor seats). */
  function seatsHeldBy(p: Principal, id: string): string[] {
    if (deps.assignmentsOf)
      return deps
        .assignmentsOf(p.company)
        .filter((x) => x.membership_id === id && x.active_to === null)
        .map((x) => x.seat_id);
    return [
      ...new Set(
        deps
          .desOf(p.company.id)
          .filter((d) => d.supervisor.membership_id === id)
          .map((d) => d.supervisor.seat_id),
      ),
    ];
  }
  /** Reads resolve the current team and reports-to names (production joins them on read). */
  const view = (p: Principal, a: Agent): Agent => {
    const toName =
      a.reports_to_kind === "digital_employee"
        ? deps.desOf(p.company.id).find((d) => d.membership_id === a.reports_to_membership_id)
            ?.display_name
        : deps.peopleOf(p).find((x) => x.id === a.reports_to_membership_id)?.name;
    const teamName = a.team_id
      ? deps.teamsOf?.(p.company).find((t) => t.id === a.team_id)?.name
      : undefined;
    // A team that no longer exists reads as none (team deletion clears it; this covers a read).
    const gone = a.team_id !== null && deps.teamsOf !== undefined && teamName === undefined;
    // HELPERS-2: the other DEs that use it, by their current name; retired ones and its home out.
    const des = deps.desOf(p.company.id);
    const usedBy = (a.used_by ?? []).flatMap((u) => {
      const d = des.find((x) => x.membership_id === u.membership_id);
      return d && d.status !== "retired" && u.membership_id !== a.reports_to_membership_id
        ? [{ ...u, name: d.display_name }]
        : [];
    });
    return {
      ...a,
      used_by: usedBy.sort((x, y) => x.name.localeCompare(y.name)),
      reports_to: { ...a.reports_to, name: toName ?? a.reports_to.name },
      ...(gone
        ? { team_id: null, team: null }
        : { team: a.team && teamName !== undefined ? { ...a.team, name: teamName } : a.team }),
      can_edit: canEdit(p, a),
    };
  };
  /**
   * Production's `assertNoneStranded`, over the state the write would leave (nothing is
   * changed): the DEs on seats at or below the touched seats with no person left above them, and
   * the non-retired agents reporting to a leaving membership or to a DE at or below the touched
   * seats whose chain no longer ends at a person. The names, sorted DEs first; empty: allowed.
   */
  function strandedAfter(p: Principal, leave: Leave): string[] {
    const members = new Set(leave.membershipIds ?? []);
    const gone = new Set(leave.retire ?? []);
    const closed = new Set(leave.closeSeats ?? []);
    const dropped = new Set((leave.drop ?? []).map((d) => `${d.seat_id}|${d.membership_id}`));
    const des = deps.desOf(p.company.id);
    const deById = new Map(des.map((d) => [d.membership_id, d]));
    const storeSeats = deps.seatsOf?.(p.company) ?? [];
    const isClosed = (id: string) =>
      closed.has(id) || storeSeats.find((s) => s.id === id)?.status === "closed";
    // Every active assignment now (the stores, else the seed's DE and supervisor seats).
    const before: { seat_id: string; membership_id: string; kind: string }[] = deps.assignmentsOf
      ? deps
          .assignmentsOf(p.company)
          .filter((a) => a.active_to === null)
          .map((a) => ({
            seat_id: a.seat_id,
            membership_id: a.membership_id,
            kind: a.membership_kind,
          }))
      : des.flatMap((d) => [
          { seat_id: d.seat_id, membership_id: d.membership_id, kind: "digital_employee" },
          {
            seat_id: d.supervisor.seat_id,
            membership_id: d.supervisor.membership_id,
            kind: "person",
          },
        ]);
    const touched = new Set(leave.seatIds ?? []);
    for (const a of before)
      if (members.has(a.membership_id) || gone.has(a.membership_id)) touched.add(a.seat_id);
    for (const s of closed) touched.add(s);
    // After the write: the leaving, dropped, closed-seat and retired DE holders are out.
    const after = before.filter(
      (a) =>
        !gone.has(a.membership_id) &&
        !dropped.has(`${a.seat_id}|${a.membership_id}`) &&
        !isClosed(a.seat_id) &&
        (a.kind !== "digital_employee" || deById.get(a.membership_id)?.status !== "retired"),
    );
    const person = (seat: string) =>
      after.some(
        (a) => a.seat_id === seat && a.kind === "person" && activePerson(p, a.membership_id),
      );
    const digital = (seat: string) =>
      after.some((a) => a.seat_id === seat && a.kind === "digital_employee");
    const up = (seat: string) => (isClosed(seat) ? null : supervisorSeatOf(p, seat));
    const below = (seat: string) => {
      const seen = new Set<string>();
      for (let s: string | null = seat; s !== null && !seen.has(s); s = up(s)) {
        if (touched.has(s)) return true;
        seen.add(s);
      }
      return false;
    };
    const ends = (seat: string) => {
      if (person(seat)) return true;
      const seen = new Set<string>([seat]);
      for (let s = up(seat); s !== null && !seen.has(s); s = up(s)) {
        if (isClosed(s)) return false;
        if (person(s)) return true;
        if (!digital(s)) return false;
        seen.add(s);
      }
      return false;
    };
    const deSeat = (id: string) =>
      after.find((a) => a.membership_id === id && a.kind === "digital_employee")?.seat_id;
    const strandedSeats = new Set(
      after
        .filter((a) => a.kind === "digital_employee")
        .map((a) => a.seat_id)
        .filter((s) => !person(s) && below(s) && !ends(s)),
    );
    const strandedDes = [
      ...new Set(
        after
          .filter((a) => a.kind === "digital_employee" && strandedSeats.has(a.seat_id))
          .map((a) => deById.get(a.membership_id)?.display_name ?? a.membership_id),
      ),
    ].sort();
    const strandedAgents = agentsOf(p)
      .filter((a) => {
        if (a.status === "retired") return false;
        const seat =
          a.reports_to_kind === "digital_employee" ? deSeat(a.reports_to_membership_id) : undefined;
        const named =
          members.has(a.reports_to_membership_id) || gone.has(a.reports_to_membership_id);
        if (!named && !(seat !== undefined && below(seat))) return false;
        if (a.reports_to_kind === "person") return !activePerson(p, a.reports_to_membership_id);
        return (
          deById.get(a.reports_to_membership_id)?.status === "retired" ||
          seat === undefined ||
          !ends(seat)
        );
      })
      .map((a) => a.name)
      .sort();
    return [...strandedDes, ...strandedAgents];
  }

  // The DE and seat sides' guards (production's `assertNoneStranded`) and the team cleanup.
  deps.expose?.({
    liveAgentsOf: (p, membershipId) =>
      agentsOf(p)
        .filter((a) => a.status !== "retired" && a.reports_to_membership_id === membershipId)
        .map((a) => a.name),
    liveAgentRowsOf: (p, membershipId) =>
      agentsOf(p)
        .filter((a) => a.status !== "retired" && a.reports_to_membership_id === membershipId)
        .map((a) => ({ id: a.id, name: a.name })),
    reportsToOf: (p, agentId) =>
      agentsOf(p).find((a) => a.id === agentId)?.reports_to_membership_id ?? null,
    strandedAfter,
    agentOf: (p, id) => agentsOf(p).find((a) => a.id === id) ?? null,
    // CHART-AGENTS: the live agents as the org chart carries them.
    chartAgentsOf: (p) =>
      agentsOf(p).flatMap((a) =>
        a.status === "retired"
          ? []
          : [
              {
                id: a.id,
                slug: a.slug,
                name: a.name,
                status: a.status,
                reports_to_membership_id: a.reports_to_membership_id,
                reports_to_kind: a.reports_to_kind,
                used_by_membership_ids: (a.used_by ?? [])
                  .map((u) => u.membership_id)
                  .filter((id) => id !== a.reports_to_membership_id),
              },
            ],
      ),
    // A deleted team leaves its agents with no team (production: workforce_agent.updated).
    clearTeam: (p, teamId) => {
      const at = deps.now().toISOString();
      for (const a of agentsOf(p))
        if (a.team_id === teamId) Object.assign(a, { team_id: null, team: null, updated_at: at });
    },
  });

  // HELPERS-2: a person adds or removes a DE's use of an agent (production's rule: whoever may
  // change the agent; its home DE, a person or a retired DE/agent is a 422).
  const setUse =
    (used: boolean) =>
    ({ principal, params }: { principal: Principal | null; params: Record<string, string> }) => {
      if (!principal) return notFound();
      const a = agentsOf(principal).find((x) => x.id === params.id);
      if (!a) return notFound();
      if (!canEdit(principal, a)) return problem(403, "forbidden", "Forbidden");
      const deId = params.membership_id ?? "";
      const list = a.used_by ?? [];
      if (used) {
        if (deId === a.reports_to_membership_id)
          return validation("membership_id: the agent reports to this digital employee already");
        if (a.status === "retired") return validation("membership_id: the agent is retired");
        const de = deps.desOf(principal.company.id).find((d) => d.membership_id === deId);
        if (!de) return validation("membership_id: no such digital employee in this company");
        if (de.status === "retired")
          return validation("membership_id: the digital employee is retired");
        if (!list.some((u) => u.membership_id === deId))
          a.used_by = [...list, { membership_id: deId, name: de.display_name, source: "manager" }];
      } else a.used_by = list.filter((u) => u.membership_id !== deId);
      return json(view(principal, a));
    };

  return {
    "workforceAgents.addUse": setUse(true),
    "workforceAgents.removeUse": setUse(false),
    "workforceAgents.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const all = url.searchParams.get("include_retired") === "true";
      const rows = agentsOf(principal)
        .filter((a) => all || a.status !== "retired")
        .map((a) => view(principal, a));
      return json(page(rows, url));
    },
    "workforceAgents.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const a = agentsOf(principal).find((x) => x.id === params.id);
      return a ? json(view(principal, a)) : notFound();
    },
    "workforceAgents.update": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (principal.roles.length === 1 && principal.roles[0] === "client_viewer")
        return problem(403, "forbidden", "Forbidden");
      // Codex WF-POLISH r1 #2: the contract's schema first (production's `readBody`).
      const parsed = parseBody("workforceAgents.update", body);
      if (parsed instanceof Response) return parsed;
      const list = agentsOf(principal);
      const i = list.findIndex((x) => x.id === params.id);
      const a = list[i];
      if (!a) return notFound();
      if (!view(principal, a).can_edit) return problem(403, "forbidden", "Forbidden");
      const b = parsed as Partial<{
        name: string;
        team_id: string | null;
        status: Agent["status"];
        reports_to_membership_id: string;
      }> &
        Record<string, unknown>;
      const next: Agent = { ...a };
      if (b.name !== undefined) next.name = b.name;
      if (b.status !== undefined) next.status = b.status;
      if (b.team_id !== undefined) {
        const t = b.team_id
          ? deps.teamsOf?.(principal.company).find((x) => x.id === b.team_id)
          : null;
        if (b.team_id && !t) return validation("team_id: no such team in this company");
        next.team_id = t?.id ?? null;
        next.team = t ? { id: t.id, name: t.name } : null;
      }
      // The reporting rule on a changed reports-to, and on a retired agent coming back (as
      // production's `changeAgent`); an unchanged reports-to alone is a no-op, not re-checked.
      const repointed =
        b.reports_to_membership_id !== undefined &&
        b.reports_to_membership_id !== a.reports_to_membership_id;
      const returning = a.status === "retired" && b.status !== undefined && b.status !== "retired";
      const target =
        repointed || returning
          ? (b.reports_to_membership_id ?? a.reports_to_membership_id)
          : undefined;
      if (target !== undefined) {
        const m = reportsToCheck(principal, target);
        if (m instanceof Response) return m;
        next.reports_to_membership_id = m.id;
        next.reports_to_kind = m.kind;
        next.reports_to = { membership_id: m.id, kind: m.kind, name: m.name };
      }
      const profile = profileOf(b);
      if (profile instanceof Response) return profile;
      Object.assign(next, profile);
      if (JSON.stringify(next) !== JSON.stringify(a)) next.updated_at = deps.now().toISOString();
      list[i] = next;
      deps.revise?.(principal, next.id, a, next);
      return json(view(principal, next));
    },
    // WF-POLISH: add an agent by hand (production's rule: a manager, or a person it will report
    // to or stand above; the slug from the name, `-2`… on a clash; a given slug taken is a 409).
    "workforceAgents.create": ({ principal, body }) => {
      if (!principal) return notFound();
      if (principal.roles.length === 1 && principal.roles[0] === "client_viewer")
        return problem(403, "forbidden", "Forbidden");
      // Codex WF-POLISH r1 #2: the contract's own schema first (strict, trimmed, limits), as the
      // API's `readBody`; then production's order: the reports-to (422), who may (403), the team.
      const parsed = parseBody("workforceAgents.create", body);
      if (parsed instanceof Response) return parsed;
      const b = parsed as {
        name: string;
        slug?: string;
        team_id?: string | null;
        reports_to_membership_id: string;
        runtime?: { container: string; profile: string; clawpanel_instance?: string | null } | null;
        status?: Agent["status"];
        role?: string | null;
        goal?: string | null;
        backstory?: string | null;
        standing_orders?: string | null;
        responsibilities?: string[];
      };
      const name = b.name;
      const m = reportsToCheck(principal, b.reports_to_membership_id);
      if (m instanceof Response) return m;
      const prospective = {
        reports_to_membership_id: m.id,
        reports_to_kind: m.kind,
      } as Agent;
      if (!canEdit(principal, prospective)) return problem(403, "forbidden", "Forbidden");
      const t = b.team_id
        ? deps.teamsOf?.(principal.company).find((x) => x.id === b.team_id)
        : null;
      if (b.team_id && !t) return validation("team_id: no such team in this company");
      const list = agentsOf(principal);
      const taken = new Set(list.map((x) => x.slug));
      let slug = b.slug;
      if (slug && taken.has(slug))
        return problem(409, "conflict", "Conflict", `an agent with slug "${slug}" exists`);
      if (!slug) {
        const base = slugOf(name);
        slug = base;
        for (let n = 2; taken.has(slug); n++) slug = `${base}-${n}`;
      }
      const profile = profileOf(b);
      if (profile instanceof Response) return profile;
      const at = deps.now().toISOString();
      const a: Agent = {
        company_id: principal.company.id,
        id: crypto.randomUUID(),
        slug,
        name,
        team_id: t?.id ?? null,
        team: t ? { id: t.id, name: t.name } : null,
        runtime: b.runtime
          ? {
              system: "hermes",
              container: b.runtime.container,
              profile: b.runtime.profile,
              clawpanel_instance: b.runtime.clawpanel_instance ?? null,
            }
          : null,
        reports_to_membership_id: m.id,
        reports_to_kind: m.kind,
        reports_to: { membership_id: m.id, kind: m.kind, name: m.name },
        status: b.status ?? (b.runtime ? "active" : "not_deployed"),
        last_activity_at: null,
        last_activity: null,
        // CLOCKWORK B2: a role, goal or backstory left out is the platform default.
        ...defaultProfile(name),
        responsibilities: [],
        standing_orders: null,
        skills: [],
        ...profile,
        can_edit: false,
        used_by: [],
        created_at: at,
        updated_at: at,
      };
      list.push(a);
      deps.revise?.(principal, a.id, null, a);
      return json(view(principal, a), 201);
    },
    // PURGE-RETIRED-AGENTS: a retired agent removed for good (managers; the name typed).
    "workforceAgents.purgePlan": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const a = agentsOf(principal).find((x) => x.id === params.id);
      if (!a) return notFound();
      return json(
        fakePurgePlan({ kind: "workforce_agent", id: a.id, name: a.name, status: a.status }),
      );
    },
    "workforceAgents.purge": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const list = agentsOf(principal);
      const a = list.find((x) => x.id === params.id);
      if (!a) return notFound();
      const b = (body ?? {}) as { confirm?: unknown; delete_memory?: unknown };
      if (!sameName(String(b.confirm ?? ""), a.name))
        return validation("confirm: type the agent's name to remove it permanently");
      const plan = fakePurgePlan({
        kind: "workforce_agent",
        id: a.id,
        name: a.name,
        status: a.status,
      });
      if (plan.blocked) return problem(409, "conflict", plan.blocked.detail);
      list.splice(list.indexOf(a), 1);
      return json(fakePurgeResult(plan, b.delete_memory === true));
    },
  };

  /** Whom an agent may report to (production's `checkReportsTo`): the member, or the 422. */
  function reportsToCheck(principal: Principal, target: string): Member | Response {
    const m = membersOf(principal).find((x) => x.id === target);
    if (!m)
      return validation(
        "reports_to_membership_id: an agent reports to a digital employee or a person",
      );
    if (m.kind === "digital_employee") {
      // Its membership must be active (production): a paused or retired DE is refused; an
      // onboarding one (active membership) is not.
      const st = liveDe(principal, m.id)?.status;
      if (st === undefined || st === "paused" || st === "retired" || st === "not_deployed")
        return validation("reports_to_membership_id: the digital employee is not active");
      // The DE must hold a seat whose chain ends at a person (the reporting rule).
      const de = liveDe(principal, m.id);
      const seated =
        de && holdersOf(principal, de.seat_id).some((h) => h.membership_id === de.membership_id);
      if (!de || !seated || !chainEndsAtPerson(principal, de.seat_id))
        return problem(
          422,
          "supervisor_required",
          "Supervisor required",
          "the digital employee's reporting chain does not end at a person",
        );
    }
    return m;
  }
}

/** A body through the route's contract schema (strict, trimmed); a refusal as a 422. */
function parseBody(
  route: "workforceAgents.create" | "workforceAgents.update",
  body: unknown,
): Record<string, unknown> | Response {
  const schema = (
    routes[route] as unknown as {
      body: {
        safeParse: (v: unknown) => {
          success: boolean;
          data?: unknown;
          error?: { issues: { path: PropertyKey[]; message: string }[] };
        };
      };
    }
  ).body;
  const r = schema.safeParse(body ?? {});
  if (!r.success) {
    const i = r.error?.issues[0];
    return validation(
      i ? `${i.path.map(String).join(".") || "body"}: ${i.message}` : "invalid body",
    );
  }
  return r.data as Record<string, unknown>;
}

/** WF-POLISH: the profile fields of a body, trimmed, blank as null (production's `profileText`). */
const PROFILE_LIMITS = { role: 2000, goal: 4000, backstory: 8000, standing_orders: 20000 } as const;
function profileOf(b: Record<string, unknown>): Partial<Agent> | Response {
  const out: Record<string, unknown> = {};
  for (const [f, max] of Object.entries(PROFILE_LIMITS)) {
    const v = b[f];
    if (v === undefined) continue;
    if (v !== null && typeof v !== "string") return validation(`${f} must be text`);
    const s = typeof v === "string" ? v.trim() : null;
    if (s && s.length > max) return validation(`${f} is too long`);
    // CLOCKWORK B2: role, goal and backstory are required (production's contract).
    if (!s && f !== "standing_orders") return validation(`${f} is required`);
    out[f] = s || null;
  }
  if (b.responsibilities !== undefined) {
    const r = b.responsibilities;
    if (!Array.isArray(r) || r.length > 50 || r.some((x) => typeof x !== "string" || !x.trim()))
      return validation("responsibilities: up to 50 non-empty lines");
    out.responsibilities = r.map((x: string) => x.trim());
  }
  // HELPERS: an agent's skill keys (production's `skillKeys`: trimmed, blanks and repeats dropped).
  if (Array.isArray(b.skills))
    out.skills = [...new Set((b.skills as unknown[]).map((x) => String(x).trim()).filter(Boolean))];
  return out as Partial<Agent>;
}

/** A DE's profile text (no own responsibilities: a DE's are its seat's). */
export const EMPTY_DE_PROFILE = {
  role: null,
  goal: null,
  backstory: null,
  standing_orders: null,
} as const satisfies NonNullable<FakeDe["profile"]>;
export function deProfileText(body: unknown): Partial<NonNullable<FakeDe["profile"]>> | Response {
  const { responsibilities: _, ...b } = (body ?? {}) as Record<string, unknown>;
  return profileOf(b) as Partial<NonNullable<FakeDe["profile"]>> | Response;
}

/** A slug from a name (production's `slugOf`). */
export function slugOf(name: string): string {
  const s = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 56)
    .replace(/-+$/, "");
  return s || "agent";
}
